/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Page from 'flarum/common/components/Page';
import type { IPageAttrs } from 'flarum/common/components/Page';
import icon from 'flarum/common/helpers/icon';
import extractText from 'flarum/common/utils/extractText';
import ItemList from 'flarum/common/utils/ItemList';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import LinkButton from 'flarum/common/components/LinkButton';
import ArticleList from './ArticleList';
import * as terms from '@fof-terms';
import ArticleListState from '../states/ArticleListState';
import type { ArticleListParams } from '../states/ArticleListState';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';
const UpdateAlert = terms.components.UpdateAlert;

/**
 * The `/articles` route: every article matching a search query.
 */
export default class ArticlesPage extends Page {
	static providesInitialSearch = true;

	articleList!: ArticleListState;

	oninit(vnode: Mithril.Vnode<IPageAttrs, this>) {
		super.oninit(vnode);

		const title = extractText(app.translator.trans('glowingblue-orell-articles.forum.articles.search_results'));

		app.history.push('of_articles', title);
		app.setTitle(title);

		// GlobalSearchState seeds the header input from `searching()` once and then
		// latches; resetting the flag makes it re-read the query on every visit here.
		(app.search as unknown as { initialValueSet: boolean }).initialValueSet = false;

		this.articleList = new ArticleListState(this.params());
		this.articleList.refresh();

		this.bodyClass = 'Articles--list';
	}

	view() {
		return (
			<div className="ArticlesPage">
				<UpdateAlert />
				<header className="Hero ArticlesSearchHero">
					<div className="container">
						<h2 className="Hero-title">
							<span className="ArticlesSearchHero-title">
								{icon('far fa-search')}
								<span>{app.translator.trans('glowingblue-orell-articles.forum.articles.search_results')}</span>
							</span>
							<span className="ArticlesSearchHero-query">
								{icon('far fa-chevron-right')}
								<span>{this.searching()}</span>
							</span>
						</h2>
					</div>
				</header>
				<div className="container">
					<div className="sideNavContainer">
						<nav className="ArticlesPage-nav sideNav">
							<label className="ArticlesPage-nav--label">{app.translator.trans('glowingblue-orell-articles.forum.articles.filter_label')}</label>
							<SelectDropdown buttonClassName="Button" className="App-titleControl">
								{this.sidebarItems().toArray()}
							</SelectDropdown>
						</nav>
						<div className="sideNavOffset ArticlesPage-results">
							<ArticleList state={this.articleList} />
						</div>
					</div>
				</div>
			</div>
		);
	}

	sidebarItems(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();

		const query = this.searching();

		items.add(
			'articles',
			<LinkButton icon="fas fa-book" href={app.route.of_articles(query)} active={true}>
				{app.translator.trans('glowingblue-orell-articles.forum.search.header')}
			</LinkButton>,
			100
		);

		items.add(
			'discussions',
			<LinkButton href={app.route('index', { q: query })} icon="far fa-comments">
				{app.translator.trans('core.forum.index.all_discussions_link')}
			</LinkButton>,
			90
		);

		items.add(
			'users',
			<LinkButton href={app.route('fof_user_directory', { q: query })} icon="far fa-address-book">
				{app.translator.trans('core.forum.search.users_heading')}
			</LinkButton>,
			80
		);

		return items;
	}

	/**
	 * Return the current search query, if any. This is implemented to activate
	 * the search box in the header.
	 *
	 * @see Search
	 */
	searching(): string | undefined {
		return this.params().q;
	}

	params(): ArticleListParams {
		return {
			q: m.route.param('q'),
		};
	}

	clearSearch() {
		const params: Record<string, unknown> = { ...this.params() };
		delete params.q;

		const routeTo = this.attrs.routeName ? this.attrs.routeName : 'of_articles';

		m.route.set(app.route(routeTo, params));
	}
}
